import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingAIAdvisorActionLink from '../../components/funding/FundingAIAdvisorActionLink';
import FundingSubmissionDetail from '../../components/funding/FundingSubmissionDetail';
import FundingSubmissionFilters from '../../components/funding/FundingSubmissionFilters';
import FundingSubmissionForm from '../../components/funding/FundingSubmissionForm';
import FundingSubmissionTable from '../../components/funding/FundingSubmissionTable';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';
import { useFundingSubmissions } from '../../hooks/funding/useFundingSubmissions';
import { useFundingDeals } from '../../hooks/funding/useFundingDeals';
import { emptyFundingSubmissionForm } from '../../services/funding/fundingSubmissionsService';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const FundingSubmissionsPage = () => {
  const navigate = useNavigate();
  const { pipeline: pipelinePath } = useFundingRoutes();
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [lenderType, setLenderType] = useState('');
  const [productType, setProductType] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState(emptyFundingSubmissionForm());
  const [formClientId, setFormClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [formError, setFormError] = useState(null);
  const [savingId, setSavingId] = useState('');
  const [creatingDealId, setCreatingDealId] = useState('');
  const [dealCreatedId, setDealCreatedId] = useState('');
  const [dealSuccessMessage, setDealSuccessMessage] = useState('');

  const agencyId = isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId;

  const { createFromSubmission } = useFundingDeals({ agencyId, autoLoad: false });

  const {
    submissions,
    loading,
    saving,
    error,
    create,
    update,
    remove,
    load,
  } = useFundingSubmissions({
    agencyId,
    filters: { status, lenderType, productType, search },
    autoLoad: Boolean(agencyId) || isPlatformContext,
  });

  const filteredSubmissions = useMemo(() => {
    if (!search.trim()) return submissions;
    const term = search.trim().toLowerCase();
    return submissions.filter((submission) => (
      (submission.clientName || '').toLowerCase().includes(term)
      || (submission.lenderName || '').toLowerCase().includes(term)
      || (submission.productType || '').toLowerCase().includes(term)
    ));
  }, [submissions, search]);

  const openCreateForm = async () => {
    setFormError(null);
    setFormValues(emptyFundingSubmissionForm());
    if (agencyId) {
      try {
        const result = await fetchFundingClients({ agencyId });
        setClients(result.clients);
        setFormClientId(result.clients[0]?.id || '');
      } catch {
        setClients([]);
      }
    }
    setFormOpen(true);
  };

  const handleCreate = async () => {
    if (!formClientId || !agencyId) {
      setFormError('Select a client and agency before creating a submission.');
      return;
    }
    setFormError(null);
    try {
      await create({
        lender_name: formValues.lender_name,
        lender_type: formValues.lender_type || undefined,
        product_type: formValues.product_type,
        requested_amount: formValues.requested_amount ? Number(formValues.requested_amount) : undefined,
        submission_method: formValues.submission_method,
        notes: formValues.notes || undefined,
      }, formClientId, agencyId);
      setFormOpen(false);
    } catch (err) {
      setFormError(err?.message || 'Failed to create submission');
    }
  };

  const handleStatusChange = async (submission, nextStatus) => {
    if (!agencyId) return;
    setSavingId(submission.id);
    try {
      const updated = await update(submission.id, { status: nextStatus }, agencyId);
      if (selectedSubmission?.id === submission.id) setSelectedSubmission(updated);
    } finally {
      setSavingId('');
    }
  };

  const handleDelete = async (submission) => {
    if (!agencyId) return;
    const confirmed = window.confirm(`Withdraw submission for ${submission.lenderName}?`);
    if (!confirmed) return;
    setSavingId(submission.id);
    try {
      await remove(submission.id, agencyId);
      if (selectedSubmission?.id === submission.id) setSelectedSubmission(null);
    } finally {
      setSavingId('');
    }
  };

  const handleCreateDeal = async (submission) => {
    if (!agencyId) return;
    const shouldCreate = window.confirm(
      `Create or open pipeline deal for ${submission.lenderName}? This links the submission to your deal pipeline.`,
    );
    if (!shouldCreate) return;

    setCreatingDealId(submission.id);
    setDealSuccessMessage('');
    try {
      const deal = await createFromSubmission(submission.id, agencyId);
      setDealCreatedId(deal?.id || submission.id);
      setDealSuccessMessage(`Deal ${deal?.stage ? `(${deal.stage})` : ''} is ready in the pipeline.`);
    } catch (err) {
      window.alert(err?.message || 'Failed to create deal');
    } finally {
      setCreatingDealId('');
    }
  };

  return (
    <FundingSectionPage
      title="Submissions"
      subtitle="Track lender submissions, underwriting status, lender responses, and follow-ups."
      activeStage="submit"
      actions={(
        <div className="flex gap-2">
          <FundingAIAdvisorActionLink label="Next submission action" prompt="Prepare submission strategy" />
          <Button className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]" onClick={openCreateForm} disabled={requiresAgencySelection}>
            New Submission
          </Button>
        </div>
      )}
    >
      <FundingPanel title="Submission tracker" subtitle="Monitor underwriting progress across your funding book">
        {requiresAgencySelection ? (
          <p className="mb-4 text-sm text-[var(--fg-muted)]">Select an agency in the header to load submissions.</p>
        ) : null}

        <div className="space-y-4">
          <FundingSubmissionFilters
            search={search}
            status={status}
            lenderType={lenderType}
            productType={productType}
            onSearchChange={setSearch}
            onStatusChange={setStatus}
            onLenderTypeChange={setLenderType}
            onProductTypeChange={setProductType}
          />

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          {dealSuccessMessage ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
              <div>{dealSuccessMessage}</div>
              <button type="button" onClick={() => navigate(pipelinePath)} className="mt-2 font-medium text-[var(--fg-accent)]">
                Open Pipeline
              </button>
            </div>
          ) : null}

          {formOpen ? (
            <div className="rounded-2xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-4">
              <div className="mb-4 text-sm font-semibold text-[var(--fg-text)]">Create submission</div>
              <label className="mb-4 block text-sm">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">Funding client</span>
                <select value={formClientId} onChange={(e) => setFormClientId(e.target.value)} className="w-full rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2 text-sm dark:bg-slate-950/20">
                  <option value="">Select client</option>
                  {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
                </select>
              </label>
              <FundingSubmissionForm values={formValues} onChange={setFormValues} onSubmit={handleCreate} saving={saving} error={formError} />
              <button type="button" onClick={() => setFormOpen(false)} className="mt-3 text-sm text-[var(--fg-muted)]">Cancel</button>
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-[var(--fg-muted)]">Loading submissions...</p>
          ) : filteredSubmissions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-10 text-center">
              <p className="text-sm font-medium text-[var(--fg-text)]">No submissions yet</p>
              <p className="mt-1 text-sm text-[var(--fg-muted)]">Create a submission from Bank Match, Application Sequence, or manually.</p>
            </div>
          ) : (
            <FundingSubmissionTable
              submissions={filteredSubmissions}
              onView={setSelectedSubmission}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
              onCreateDeal={handleCreateDeal}
              savingId={savingId}
              creatingDealId={creatingDealId}
            />
          )}
        </div>
      </FundingPanel>

      {selectedSubmission ? (
        <FundingPanel title="Submission detail" subtitle="Underwriting package, documents, and lender response">
          <FundingSubmissionDetail
            submission={selectedSubmission}
            onClose={() => setSelectedSubmission(null)}
            onCreateDeal={handleCreateDeal}
            creatingDeal={creatingDealId === selectedSubmission.id}
            dealCreated={dealCreatedId === selectedSubmission.id}
          />
        </FundingPanel>
      ) : null}
    </FundingSectionPage>
  );
};

export default FundingSubmissionsPage;
